/**
 * Party identity form — create (`POST /parties`, client-key idempotent) and
 * header edit (`PATCH /parties/{id}`, required strong If-Match). `kind` is
 * immutable after create, so the edit mode renders it read-only.
 *
 * Tax registration is an atomic pair: the contract's `PartnersTaxRegistration`
 * requires BOTH country_code and registration_no, so the form refuses a half
 * pair client-side rather than letting the server 400 on a partial object.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { PARTY_KINDS, type PartyDetail, type PartyKind } from "../api";

const CURRENCY_RE = /^[A-Z]{3}$/;
const COUNTRY_RE = /^[A-Z]{2}$/;

export interface PartyFormValues {
  kind: PartyKind;
  displayName: string;
  defaultCurrency: string;
  taxCountry: string;
  taxNumber: string;
  /** Edit mode only: explicitly null out an existing tax registration. */
  clearTaxRegistration: boolean;
}

export type PartyFormErrors = Partial<Record<keyof PartyFormValues, string>>;

export interface PartyFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  /** Existing aggregate in edit mode; seeds the initial values. */
  party?: PartyDetail;
  pending: boolean;
  /** Server field errors already mapped onto form field names. */
  serverErrors?: PartyFormErrors;
  onSubmit: (values: PartyFormValues) => void;
  onOpenChange: (open: boolean) => void;
}

function initialValues(mode: "create" | "edit", party?: PartyDetail): PartyFormValues {
  if (mode === "edit" && party !== undefined) {
    return {
      kind: party.kind,
      displayName: party.display_name,
      defaultCurrency: party.default_currency ?? "",
      taxCountry: party.tax_registration?.country_code ?? "",
      taxNumber: party.tax_registration?.registration_no ?? "",
      clearTaxRegistration: false,
    };
  }
  return {
    kind: "org",
    displayName: "",
    defaultCurrency: "SAR",
    taxCountry: "",
    taxNumber: "",
    clearTaxRegistration: false,
  };
}

export function PartyFormDialog({
  open,
  mode,
  party,
  pending,
  serverErrors,
  onSubmit,
  onOpenChange,
}: PartyFormDialogProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<PartyFormValues>(() => initialValues(mode, party));
  const [errors, setErrors] = useState<PartyFormErrors>({});

  // Reopening the dialog (or switching party) must not show stale input.
  useEffect(() => {
    if (open) {
      setValues(initialValues(mode, party));
      setErrors({});
    }
  }, [open, mode, party]);

  const merged: PartyFormErrors = { ...errors, ...serverErrors };
  const hasTaxRegistration = party?.tax_registration !== undefined;

  const set = <K extends keyof PartyFormValues>(key: K, value: PartyFormValues[K]): void => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = (): void => {
    const next: PartyFormErrors = {};
    if (values.displayName.trim() === "") next.displayName = t("partners.create.nameRequired");
    const currency = values.defaultCurrency.trim().toUpperCase();
    if (!CURRENCY_RE.test(currency)) next.defaultCurrency = t("partners.create.currencyInvalid");
    const country = values.taxCountry.trim().toUpperCase();
    const number = values.taxNumber.trim();
    const clearing = values.clearTaxRegistration;
    if (!clearing && (country !== "" || number !== "")) {
      if (country === "" || number === "") {
        next.taxNumber = t("partners.create.taxIncomplete");
      } else if (!COUNTRY_RE.test(country)) {
        next.taxCountry = t("partners.create.countryInvalid");
      }
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      ...values,
      displayName: values.displayName.trim(),
      defaultCurrency: currency,
      taxCountry: country,
      taxNumber: number,
    });
  };

  const fieldError = (key: keyof PartyFormValues) =>
    merged[key] === undefined ? null : (
      <p role="alert" className="text-sm text-destructive">
        {merged[key]}
      </p>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t("actions.close")}>
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? t("partners.create.title") : t("partners.edit.title")}
          </DialogTitle>
          <DialogDescription>
            {mode === "create" ? t("partners.create.description") : t("partners.edit.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="party-kind">{t("partners.create.kind")}</Label>
            <Select
              value={values.kind}
              disabled={mode === "edit"}
              onValueChange={(kind) => set("kind", kind as PartyKind)}
            >
              <SelectTrigger id="party-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PARTY_KINDS.map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {t(`partners.kind.${kind}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="party-currency">{t("partners.create.defaultCurrency")}</Label>
            <Input
              id="party-currency"
              dir="ltr"
              maxLength={3}
              autoComplete="off"
              placeholder={t("partners.create.currencyPlaceholder")}
              value={values.defaultCurrency}
              aria-invalid={merged.defaultCurrency !== undefined}
              onChange={(event) => set("defaultCurrency", event.target.value.toUpperCase())}
            />
            {fieldError("defaultCurrency")}
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="party-display-name">{t("partners.create.displayName")}</Label>
            <Input
              id="party-display-name"
              dir="auto"
              value={values.displayName}
              aria-invalid={merged.displayName !== undefined}
              onChange={(event) => set("displayName", event.target.value)}
            />
            {fieldError("displayName")}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="party-tax-country">{t("partners.create.taxCountry")}</Label>
            <Input
              id="party-tax-country"
              dir="ltr"
              maxLength={2}
              autoComplete="off"
              placeholder={t("partners.create.taxCountryPlaceholder")}
              value={values.taxCountry}
              disabled={values.clearTaxRegistration}
              aria-invalid={merged.taxCountry !== undefined}
              onChange={(event) => set("taxCountry", event.target.value.toUpperCase())}
            />
            {fieldError("taxCountry")}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="party-tax-number">{t("partners.create.taxNumber")}</Label>
            <Input
              id="party-tax-number"
              dir="ltr"
              autoComplete="off"
              value={values.taxNumber}
              disabled={values.clearTaxRegistration}
              aria-invalid={merged.taxNumber !== undefined}
              onChange={(event) => set("taxNumber", event.target.value)}
            />
            {fieldError("taxNumber")}
          </div>

          <p className="text-xs text-muted-foreground sm:col-span-2">
            {t("partners.create.taxHint")}
          </p>

          {mode === "edit" && hasTaxRegistration ? (
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox
                id="party-clear-tax"
                checked={values.clearTaxRegistration}
                onCheckedChange={(checked) => set("clearTaxRegistration", checked === true)}
              />
              <Label htmlFor="party-clear-tax" className="font-normal">
                {t("partners.edit.clearTax")}
              </Label>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("actions.cancel")}
          </Button>
          <Button type="button" loading={pending} onClick={submit}>
            {mode === "create" ? t("partners.create.submit") : t("partners.edit.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
